import React, { useState, useEffect } from 'react'; // ✅ CORRECTION : Ajout de useState et useEffect
import { CheckCircle, Clock, AlertCircle, Search, Brain, HelpingHand, CheckSquare } from 'lucide-react';
import { Progress } from '@/components/ui/Progress';
import { Card, CardContent } from '@/components/ui/Card';
import { cn } from '@/utils/cn';

export type AnalysisStatus = 
  | 'pending'
  | 'fetching'
  | 'analyzing'
  | 'generating_questions'
  | 'completed'
  | 'error';

interface AnalysisStep {
  id: AnalysisStatus;
  label: string;
  description: string;
  icon: React.ReactNode;
  estimatedTime: string;
}

interface AnalysisProgressProps {
  status: AnalysisStatus;
  progress: number;
  errorMessage?: string;
  className?: string;
  onRetry?: () => void;
}

const ANALYSIS_STEPS: AnalysisStep[] = [
  {
    id: 'pending',
    label: 'En attente',
    description: 'Préparation de l\'analyse',
    icon: <Clock className="w-5 h-5" />,
    estimatedTime: '< 5s'
  },
  {
    id: 'fetching',
    label: 'Récupération ADEME',
    description: 'Récupération des données depuis l\'API ADEME',
    icon: <Search className="w-5 h-5" />,
    estimatedTime: '5-10s'
  },
  {
    id: 'analyzing',
    label: 'Analyse DPE',
    description: 'Application des 28 règles métier',
    icon: <Brain className="w-5 h-5" />,
    estimatedTime: '10-15s'
  },
  {
    id: 'generating_questions',
    label: 'Questions contextuelles',
    description: 'Génération des questions personnalisées',
    icon: <HelpingHand className="w-5 h-5" />,
    estimatedTime: '3-5s'
  },
  {
    id: 'completed',
    label: 'Analyse complète',
    description: 'Tous les traitements sont terminés',
    icon: <CheckSquare className="w-5 h-5" />,
    estimatedTime: ''
  }
];

export const AnalysisProgress: React.FC<AnalysisProgressProps> = ({
  status,
  progress,
  errorMessage,
  className,
  onRetry
}) => {
  // Animation states for smooth transitions
  const [animatedSteps, setAnimatedSteps] = useState<Set<string>>(new Set());
  
  // Animate steps based on progress
  useEffect(() => {
    const newAnimatedSteps = new Set<string>();
    
    if (progress >= 20) newAnimatedSteps.add('pending');
    if (progress >= 40) newAnimatedSteps.add('fetching');
    if (progress >= 70) newAnimatedSteps.add('analyzing');
    if (progress >= 90) newAnimatedSteps.add('generating_questions');
    if (progress >= 100) newAnimatedSteps.add('completed');
    
    setAnimatedSteps(newAnimatedSteps);
  }, [progress]);
  
  // Find current step index
  const currentStepIndex = ANALYSIS_STEPS.findIndex(step => step.id === status);
  
  // Calculate overall progress
  const calculateOverallProgress = (): number => {
    if (status === 'completed') return 100;
    if (status === 'error') return Math.max(20, progress);
    
    const stepsCount = ANALYSIS_STEPS.length - 1; // Exclude 'completed'
    const baseProgress = (currentStepIndex / stepsCount) * 100;
    const stepProgress = (progress / 100) * (100 / stepsCount);
    
    return Math.min(99, baseProgress + stepProgress);
  };

  return (
    <Card className={cn('overflow-hidden', className)}>
      <CardContent className="p-6">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-900">
              Progression de l'analyse
            </h3>
            <span className="text-sm font-medium">
              {Math.round(calculateOverallProgress())}%
            </span>
          </div>
          
          <Progress
            value={calculateOverallProgress()}
            variant={status === 'error' ? 'danger' : 'default'}
            size="md"
            animated={status !== 'completed' && status !== 'error'}
          />
        </div>
        
        {status === 'error' && errorMessage && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-red-700 mb-1">Erreur lors de l'analyse</h4>
              <p className="text-sm text-red-600">{errorMessage}</p>
              {onRetry && (
                <button
                  onClick={onRetry}
                  className="mt-2 text-sm font-medium text-red-700 hover:text-red-800"
                >
                  Réessayer
                </button>
              )}
            </div>
          </div>
        )}
        
        <div className="space-y-4">
          {ANALYSIS_STEPS.map((step, index) => {
            // Determine step status
            const isCompleted = animatedSteps.has(step.id);
            const isActive = !isCompleted && (
              (step.id === 'pending' && progress < 20) ||
              (step.id === 'fetching' && progress >= 20 && progress < 40) ||
              (step.id === 'analyzing' && progress >= 40 && progress < 70) ||
              (step.id === 'generating_questions' && progress >= 70 && progress < 90) ||
              (step.id === 'completed' && progress >= 90)
            );
            const isPending = !isCompleted && !isActive;
            const isError = status === 'error' && index === currentStepIndex;
            
            return (
              <div 
                key={step.id}
                className={cn(
                  'flex items-start p-3 rounded-lg transition-all duration-500',
                  isActive && 'bg-blue-50 scale-105',
                  isCompleted && 'bg-green-50',
                  isError && 'bg-red-50',
                  isCompleted && 'animate-step-complete'
                )}
              >
                <div className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0 transition-all duration-300',
                  isActive && 'bg-blue-100 text-blue-600 animate-pulse scale-110',
                  isCompleted && 'bg-green-100 text-green-600 scale-110',
                  isPending && 'bg-gray-100 text-gray-400',
                  isError && 'bg-red-100 text-red-600'
                )}>
                  {isCompleted ? (
                    <CheckCircle className="w-5 h-5 animate-zoom-in" />
                  ) : isError ? (
                    <AlertCircle className="w-5 h-5" />
                  ) : (
                    step.icon
                  )}
                </div>
                
                <div>
                  <div className="flex items-center">
                    <h4 className={cn(
                      'font-medium',
                      isActive && 'text-blue-700',
                      isCompleted && 'text-green-700',
                      isPending && 'text-gray-500',
                      isError && 'text-red-700'
                    )}>
                      {step.label}
                    </h4>
                    
                    {step.estimatedTime && !isCompleted && !isError && (
                      <span className="ml-2 text-xs text-gray-500">
                        {step.estimatedTime}
                      </span>
                    )}
                  </div>
                  
                  <p className={cn(
                    'text-sm',
                    isActive && 'text-blue-600',
                    isCompleted && 'text-green-600',
                    isPending && 'text-gray-400',
                    isError && 'text-red-600'
                  )}>
                    {step.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default AnalysisProgress;